import type {
  AgentTestScenarioResponse,
  CreateAgentTestScenarioRequest,
  GoldenSignal,
  UpdateAgentTestScenarioRequest,
} from '@troia-v3/types';

// Re-export para consumidores do módulo (PreDeployTestModal, etc.)
export type {
  GoldenCaseComparison,
  GoldenCaseTurnComparison,
  GoldenSignal,
} from '@troia-v3/types';

import { api } from '@/lib/api';
import { getApiBaseUrl } from '@/lib/config';

/**
 * Tipos do módulo agent-training — espelho de
 * `TROIA-V3-BACKEND/src/modules/agent-training/types.ts`. Redefinidos
 * localmente porque não vão pro @troia-v3/types (shapes internos
 * derivados do Langfuse). Mesmo padrão do agentAnalyticsApi.
 */

export type TrainingScenarioType = 'quick' | 'full' | 'custom' | 'manual';

export type TrainingStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface ScenarioEvaluatorScore {
  name: string;
  value: number;
  comment: string | null;
}

export interface ScenarioResult {
  scenarioId: string;
  scenarioLabel: string;
  /** Turnos sequenciais do cliente (1+). Último turno é o avaliado. */
  customerTurns: string[];
  traceId: string;
  agentResponse: string;
  latencyMs: number;
  toolCalls: string[];
  scores: Record<string, ScenarioEvaluatorScore>;
  passed: boolean;
  toolsOk: boolean | null;
}

export interface SnapshotSummary {
  totalScenarios: number;
  relevancePassed: number;
  relevanceFailed: number;
  toolExpectationsOk: number;
  toolExpectationsFail: number;
  /** Médias ponderadas (0-1) por evaluator — 13 evaluators v6.1 (com actionCompleteness) */
  averages: {
    relevance: number;
    hallucination: number;
    tone: number;
    toolUsage: number;
    answerCompleteness: number;
    conversationAdvancement: number;
    humanization: number;
    scopeAdherence: number;
    conciseness: number;
    knowledgeRetention: number;
    /** v6 (RAGAS): Fração de claims com apoio no RAG consultado (1.0 quando sem RAG). */
    faithfulness: number;
    /** v6 (RAGAS): Progresso da trajetória em direção ao goal inferido do cliente. */
    agentGoalAccuracy: number;
    /** v6.1: 1.0 quando agente chamou tool p/ ação afirmada; 0.0 se fabricou (fabricação grave). */
    actionCompleteness: number;
  };
  overallScore: number;
}

/**
 * Informação de regressão — presente quando o snapshot piorou em relação
 * ao anterior bem-sucedido. Usado pelo banner de alerta proativo do
 * Coach v2 (tela de resultados do treino + aba Saúde).
 */
export interface SnapshotRegressionInfo {
  previousOverallScore: number;
  overallScoreDelta: number;
  regressedEvaluators: Array<{
    metric:
      | 'relevance'
      | 'hallucination'
      | 'tone'
      | 'toolUsage'
      | 'answerCompleteness'
      | 'conversationAdvancement'
      | 'scopeAdherence'
      | 'conciseness'
      | 'knowledgeRetention'
      | 'humanization'
      | 'faithfulness'
      | 'agentGoalAccuracy'
      | 'actionCompleteness';
    delta: number;
    from: number;
    to: number;
  }>;
  previousSnapshotId: string;
  previousSnapshotCreatedAt: string;
}

export interface AgentQualitySnapshotResponse {
  id: string;
  agentId: string;
  agentVersionId?: string;
  scenarioType: TrainingScenarioType;
  status: TrainingStatus;
  model: string;
  agentName: string;
  /** Snapshot do systemPrompt no momento do run — usado no diff do Coach v3. */
  systemPromptSnapshot: string;
  progress: {
    completedScenarios: number;
    totalScenarios: number;
    currentStepLabel: string;
  };
  scenarios: ScenarioResult[];
  summary: SnapshotSummary | null;
  regressionInfo?: SnapshotRegressionInfo | null;
  /** Ver Test Before Deploy — marca snapshots criados pelo fluxo pré-deploy */
  isPreDeploy?: boolean;
  /** Coach v3 iterativo: presente no snapshot-pai de runs iterativos. */
  iterativeInfo?: IterativeTrainingInfo;
  error: string | null;
  creditCost: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string;
}

export interface IterativeTrainingInfo {
  parentSnapshotId?: string;
  iteration?: number;
  maxIterations: number;
  qualityThreshold: number;
  autoApply: boolean;
  completedIterations?: number;
  stopReason?: 'threshold' | 'stagnation' | 'max_iterations' | 'no_improvement';
  bestScore?: number;
  bestPromptSnapshotId?: string;
  /** Prompt vencedor da otimização — usado pra aplicar no agente e renderizar diff. */
  bestPrompt?: string;
  autoApplied?: boolean;
  initialScore?: number;
  iterations?: IterationResult[];
}

// ─── Start training ──────────────────────────────────────────────────────

export interface StartTrainingRequest {
  scenarioType: TrainingScenarioType;
  customScenarios?: Array<{
    label: string;
    /** Turnos do cliente (1+). Cenário multi-turn por default. */
    customerTurns: string[];
  }>;
}

export interface StartTrainingResponse {
  snapshotId: string;
  status: TrainingStatus;
  estimatedDurationSeconds: number;
  creditCost: number;
}

// ─── Prompt Coach ────────────────────────────────────────────────────────

export interface PromptSuggestion {
  id: string;
  title: string;
  description: string;
  evidence: Array<{
    scenarioId: string;
    snippet: string;
    /** Turnos do cliente que geraram a resposta do agente. Pode vir
     *  ausente em snapshots antigos cacheados antes da v1.2.71. */
    customerTurns?: string[];
  }>;
  proposedChange: string;
  priority: 'low' | 'medium' | 'high';
  relatedMetrics: string[];
}

export interface GenerateSuggestionsResponse {
  snapshotId: string;
  suggestions: PromptSuggestion[];
  hasSuggestions: boolean;
  fallbackReason?: 'no_openai' | 'llm_error' | 'no_snapshot' | 'parse_error';
}

// ─── Apply ───────────────────────────────────────────────────────────────

export interface ApplyTrainingRequest {
  snapshotId: string;
  selectedSuggestionIds: string[];
  userInstructions?: string;
}

export interface ApplyTrainingResponse {
  /**
   * ID do snapshot que originou essa aplicação — o frontend propaga
   * até o salvamento do agente via `baseSnapshotId` no updateAIAgent.
   */
  snapshotId: string;
  newSystemPrompt: string;
  previousSystemPrompt: string;
  changeSummary: string;
  success: boolean;
  error?: string;
}

// ─── Version metrics (Sprint UI-4) ───────────────────────────────────────

export interface AgentVersionMetricsResponse {
  versionId: string;
  agentId: string;
  snapshotsCount: number;
  completedSnapshotsCount: number;
  latestSnapshot: AgentQualitySnapshotResponse | null;
  averageOverallScore: number | null;
  averages: SnapshotSummary['averages'] | null;
}

// ─── Test Before Deploy (Opção A) ────────────────────────────────────────

/**
 * Request pro endpoint `POST /test-before-deploy`. Cliente envia o
 * prompt que quer testar + opcionalmente o modelo override + o tipo
 * do teste.
 */
export interface TestBeforeDeployRequest {
  systemPrompt: string;
  model?: string;
  scenarioType: 'quick' | 'full';
}

export interface TestBeforeDeployResponse {
  snapshotId: string;
  status: TrainingStatus;
  /** true quando resultado veio do cache in-memory (10min TTL) */
  fromCache: boolean;
  creditCost: number;
  estimatedDurationSeconds: number;
}

export type TestComparisonRecommendation = 'safe' | 'warning' | 'danger';

export interface TestComparisonEvaluatorDelta {
  metric:
    | 'relevance'
    | 'hallucination'
    | 'tone'
    | 'toolUsage'
    | 'answerCompleteness'
    | 'conversationAdvancement'
    | 'humanization'
    | 'scopeAdherence'
    | 'conciseness'
    | 'knowledgeRetention'
    | 'faithfulness'
    | 'agentGoalAccuracy';
  from: number;
  to: number;
  delta: number;
}

export interface TestBeforeDeployComparisonResponse {
  snapshot: AgentQualitySnapshotResponse;
  comparison: {
    previousOverallScore: number;
    currentOverallScore: number;
    overallScoreDelta: number;
    recommendation: TestComparisonRecommendation;
    regressedEvaluators: TestComparisonEvaluatorDelta[];
    improvedEvaluators: TestComparisonEvaluatorDelta[];
    previousSnapshotId: string;
    previousSnapshotCreatedAt: string;
  } | null;
  /**
   * Sinal complementar do Golden Dataset — respostas de conversas reais
   * que o usuário marcou como EXCELENTE. `null` quando o agente não
   * tem nenhum golden case ativo (fluxo sintético segue normal).
   */
  goldenSignal: GoldenSignal | null;
}

// ─── Iterative training (dead-letter) ───────────────────────────────────
// Coach v3 ("Otimização Iterativa") foi removido em 2026-05-12.
// O type `IterationResult` permanece apenas para retrocompat com snapshots
// históricos no Mongo que ainda carregam `iterativeInfo.iterations[]`.

export interface IterationResult {
  iteration: number;
  snapshotId: string;
  score: number;
  delta: number;
  rollback: boolean;
  suggestionsApplied: number;
  promptLength: number;
}

// ─── Scenarios (Entrega 3) ───────────────────────────────────────────────

/**
 * Filtros da listagem de cenários. `agentCategory` + `agentSegment` usam a
 * lógica M-para-M do backend: retornam cenários que se aplicam à tupla
 * (cenários universais + específicos da categoria + específicos do segmento
 * + cruzados). `isSystem` permite filtrar só system OU só custom.
 */
export interface ListScenariosQuery {
  agentCategory?: string;
  agentSegment?: string;
  isSystem?: boolean;
  activeOnly?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ListScenariosResponse {
  items: AgentTestScenarioResponse[];
  total: number;
}

// ─── Compare ─────────────────────────────────────────────────────────────

export interface CompareSnapshotsResponse {
  from: {
    id: string;
    createdAt: string;
    summary: SnapshotSummary;
  };
  to: {
    id: string;
    createdAt: string;
    summary: SnapshotSummary;
  };
  delta: {
    relevance: number;
    hallucination: number;
    tone: number;
    toolUsage: number;
    answerCompleteness: number;
    conversationAdvancement: number;
    humanization: number;
    scopeAdherence: number;
    conciseness: number;
    knowledgeRetention: number;
    faithfulness: number;
    agentGoalAccuracy: number;
    overallScore: number;
  };
}

// ─── API service ─────────────────────────────────────────────────────────

export const agentTrainingApi = {
  async startTraining(
    agentId: string,
    input: StartTrainingRequest,
  ): Promise<StartTrainingResponse> {
    const response = await api.post(
      `agent-training/${agentId}/train`,
      input,
    );
    return response.data.data;
  },

  async getSnapshot(snapshotId: string): Promise<AgentQualitySnapshotResponse> {
    const response = await api.get(`agent-training/snapshots/${snapshotId}`);
    return response.data.data;
  },

  async listSnapshots(
    agentId: string,
    limit = 20,
  ): Promise<AgentQualitySnapshotResponse[]> {
    const response = await api.get(`agent-training/${agentId}/snapshots`, {
      params: { limit },
    });
    return response.data.data.items;
  },

  async generateSuggestions(
    agentId: string,
    snapshotId: string,
  ): Promise<GenerateSuggestionsResponse> {
    const response = await api.post(
      `agent-training/${agentId}/suggestions`,
      { snapshotId },
    );
    return response.data.data;
  },

  async applyTraining(
    agentId: string,
    input: ApplyTrainingRequest,
  ): Promise<ApplyTrainingResponse> {
    const response = await api.post(`agent-training/${agentId}/apply`, input);
    return response.data.data;
  },

  /**
   * Versão streaming do `applyTraining` — consome Server-Sent Events (SSE)
   * do endpoint `/agent-training/:agentId/apply/stream` e invoca `onChunk`
   * a cada delta de texto que chega do modelo. Retorna o resultado final
   * quando o stream termina.
   *
   * Usa `fetch` nativo em vez de axios porque axios não suporta streaming
   * de response bodies (`responseType: 'stream'` só funciona no Node). O
   * auth token é adicionado manualmente já que não passamos pelos
   * interceptors do axios.
   *
   * Tipos de evento SSE:
   * - `{ type: 'chunk', text: string }` — delta de texto do LLM
   * - `{ type: 'done', result: ApplyTrainingResponse }` — resultado final
   * - `{ type: 'error', error: string }` — erro inesperado (throw)
   *
   * Em caso de erro de rede ou stream, lança `Error` — caller trata.
   */
  async applyTrainingStream(
    agentId: string,
    input: ApplyTrainingRequest,
    onChunk: (text: string) => void,
  ): Promise<ApplyTrainingResponse> {
    const token =
      localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const baseUrl = getApiBaseUrl();

    const response = await fetch(
      `${baseUrl}/agent-training/${agentId}/apply/stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(input),
      },
    );

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(
        `HTTP ${response.status}: ${text.slice(0, 200) || response.statusText}`,
      );
    }

    if (!response.body) {
      throw new Error('Response body streaming não disponível');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: ApplyTrainingResponse | null = null;

    // Parse SSE lines conforme chegam. Cada evento termina em `\n\n` — usamos
    // split('\n') e preservamos a última linha parcial no buffer.
    let streamDone = false;
    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) {
        streamDone = true;
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith('data:')) continue;

        const payload = line.slice(5).trim();
        if (!payload) continue;

        let event:
          | { type: 'chunk'; text: string }
          | { type: 'done'; result: ApplyTrainingResponse }
          | { type: 'error'; error: string };
        try {
          event = JSON.parse(payload);
        } catch {
          // Linha parcial ou JSON malformado — ignora
          continue;
        }

        if (event.type === 'chunk') {
          onChunk(event.text);
        } else if (event.type === 'done') {
          finalResult = event.result;
        } else if (event.type === 'error') {
          throw new Error(event.error);
        }
      }
    }

    if (!finalResult) {
      throw new Error('Stream terminou sem evento "done"');
    }
    return finalResult;
  },

  async compareSnapshots(
    agentId: string,
    fromSnapshotId: string,
    toSnapshotId: string,
  ): Promise<CompareSnapshotsResponse> {
    const response = await api.get(`agent-training/${agentId}/compare`, {
      params: { from: fromSnapshotId, to: toSnapshotId },
    });
    return response.data.data;
  },

  /**
   * Métricas agregadas dos snapshots vinculados a uma versão do agente
   * (Sprint UI-4). Retorna `latestSnapshot=null` quando nunca houve treino
   * pra essa versão.
   */
  async getVersionMetrics(
    agentId: string,
    versionId: string,
  ): Promise<AgentVersionMetricsResponse> {
    const response = await api.get(
      `agent-training/${agentId}/versions/${versionId}/metrics`,
    );
    return response.data.data;
  },

  // ─── Test Before Deploy (Opção A) ──────────────────────────────────────

  /**
   * Dispara teste pré-deploy de um prompt arbitrário contra os cenários
   * do agente. Não muta o agente — apenas roda os cenários com o prompt
   * fornecido como override.
   *
   * Se `fromCache: true`, o resultado é instantâneo (mesmo prompt foi
   * testado nos últimos 10min) e `status` já vem como `completed`.
   * Caller pode pular direto pro `getTestBeforeDeployComparison` sem
   * polling.
   */
  async testBeforeDeploy(
    agentId: string,
    input: TestBeforeDeployRequest,
  ): Promise<TestBeforeDeployResponse> {
    const response = await api.post(
      `agent-training/${agentId}/test-before-deploy`,
      input,
    );
    return response.data.data;
  },

  /**
   * Retorna o snapshot do teste pré-deploy já concluído + comparison
   * com a baseline. Chamar apenas quando o snapshot tem `status='completed'`.
   */
  async getTestBeforeDeployComparison(
    agentId: string,
    snapshotId: string,
  ): Promise<TestBeforeDeployComparisonResponse> {
    const response = await api.get(
      `agent-training/${agentId}/test-before-deploy/${snapshotId}/compare`,
    );
    return response.data.data;
  },

  // ─── Scenarios CRUD (Entrega 3) ────────────────────────────────────────

  /**
   * Lista cenários de teste (system + custom do tenant). Aceita filtros
   * M-para-M por categoria/segmento do agente — o backend retorna
   * cenários aplicáveis (universais + categoria-específicos + segmento-
   * específicos + cruzados).
   */
  async listScenarios(
    query: ListScenariosQuery = {},
  ): Promise<ListScenariosResponse> {
    const params: Record<string, string | number | boolean> = {};
    if (query.agentCategory) params.agentCategory = query.agentCategory;
    if (query.agentSegment) params.agentSegment = query.agentSegment;
    if (query.isSystem !== undefined) params.isSystem = query.isSystem;
    if (query.activeOnly !== undefined) params.activeOnly = query.activeOnly;
    if (query.search) params.search = query.search;
    if (query.page !== undefined) params.page = query.page;
    if (query.limit !== undefined) params.limit = query.limit;

    const response = await api.get('agent-training/scenarios', { params });
    return response.data.data;
  },

  /**
   * Cria um cenário customizado do tenant. Cenários do sistema (isSystem=true)
   * não podem ser criados por essa rota — backend ignora e sempre seta
   * isSystem=false. `scenarioKey` deve ser kebab-case único no tenant.
   */
  async createScenario(
    data: CreateAgentTestScenarioRequest,
  ): Promise<AgentTestScenarioResponse> {
    const response = await api.post('agent-training/scenarios', data);
    return response.data.data;
  },

  /**
   * Atualiza cenário customizado. Backend rejeita com 403 se o cenário
   * for do sistema (isSystem=true).
   */
  async updateScenario(
    scenarioId: string,
    data: UpdateAgentTestScenarioRequest,
  ): Promise<AgentTestScenarioResponse> {
    const response = await api.patch(
      `agent-training/scenarios/${scenarioId}`,
      data,
    );
    return response.data.data;
  },

  /**
   * Soft delete de cenário customizado. Backend rejeita com 403 se o
   * cenário for do sistema.
   */
  async deleteScenario(scenarioId: string): Promise<void> {
    await api.delete(`agent-training/scenarios/${scenarioId}`);
  },
};
